import { compact, isUndefined } from 'lodash';
import { useEffect } from 'react';

import {
  AssetIntegrationFormShape,
  defaultValues,
} from '@/modules/assets/AssetIntegrationField/AssetIntegrationField.types';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';

import {
  LinkIntegrationEntityDataQuery,
  useLinkIntegrationEntityDataQuery,
} from './graphql/LinkIntegrationEntityFormModal.generated';

export function getFormDataFromQueryData(
  data: LinkIntegrationEntityDataQuery
): AssetIntegrationFormShape {
  const entity = getNodes(data.entities)[0];

  // this shouldn't happen! just being defensive.
  if (!entity) {
    diagnostics.error('Entity not found', undefined);
    return defaultValues;
  }

  return {
    integrationEntityIds: compact(
      entity.integrationEntities?.map((ie) => ie.id)
    ),
    linkToAllAddeparEntities: entity.addeparLinkedToNongrantorEntity ?? false,
    // this is a little confusing, but we store it in the db as the inverse of showAllIntegratedHoldings
    // so we need to invert it here
    showAllIntegratedHoldings: isUndefined(entity.groupIntegrationAssetValues)
      ? false
      : !entity.groupIntegrationAssetValues,

    // we don't allow toggling additional manual assets in this form
    allowAdditionalManualAssets: false,
  };
}
export function useLinkIntegrationFormData(
  entityId: string,
  isModalOpen: boolean,
  reset: (data: AssetIntegrationFormShape) => void
) {
  const queryProps = useLinkIntegrationEntityDataQuery({
    variables: { entityId },
    skip: !isModalOpen,
  });

  // we expose this directly so we can compare to the new values from the form and
  // determine what we need to add/remove
  const previousIntegrationEntityIds = (() => {
    if (!queryProps.data) {
      return [];
    }

    const formValues = getFormDataFromQueryData(queryProps.data);
    return compact(formValues.integrationEntityIds);
  })();

  const entityType = (() => {
    if (!queryProps.data) {
      return null;
    }

    const entity = getNodes(queryProps.data.entities)[0];

    if (!entity) {
      return null;
    }

    return getEntityTypeFromEntityKind(entity.kind);
  })();

  useEffect(() => {
    if (queryProps.data) {
      reset(getFormDataFromQueryData(queryProps.data));
    }
  }, [queryProps.data, reset]);

  return {
    ...queryProps,
    previousIntegrationEntityIds,
    entityType,
  };
}
